import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { STATUS_CLASSES, STATUS_LABELS } from "@/lib/crmConstants";
import { Users, TrendingUp, Trophy, Eye } from "lucide-react";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  BarChart,
  Bar,
} from "recharts";

const StatCard = ({ icon: Icon, label, value, accent }) => (
  <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
    <CardContent className="p-5 flex items-center gap-4">
      <span
        className={`h-11 w-11 rounded-xl flex items-center justify-center border ${
          accent
            ? "bg-[rgba(202,255,26,0.12)] border-[rgba(202,255,26,0.3)] text-[color:var(--hs-lime)]"
            : "bg-white/5 border-white/15 text-white/70"
        }`}
      >
        <Icon className="h-5 w-5" />
      </span>
      <div>
        <div className="font-display text-3xl text-white">{value}</div>
        <div className="text-xs text-white/50 mt-0.5">{label}</div>
      </div>
    </CardContent>
  </Card>
);

const chartTooltipStyle = {
  backgroundColor: "#151821",
  border: "1px solid rgba(255,255,255,0.15)",
  borderRadius: "12px",
  color: "#f2f2f2",
  fontSize: "12px",
};

export const OverviewTab = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/dashboard/stats")
      .then(({ data }) => setStats(data))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-24 rounded-2xl bg-white/5" />
        ))}
      </div>
    );
  }

  if (!stats) return <p className="text-white/50 text-sm">Could not load dashboard stats.</p>;

  const statusChartData = stats.by_status.map((row) => ({
    name: STATUS_LABELS[row.status] || row.status,
    count: row.count,
  }));

  return (
    <div className="space-y-6" data-testid="crm-overview">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="crm-stats-cards">
        <StatCard icon={Users} label="Total leads" value={stats.total_leads} accent />
        <StatCard icon={TrendingUp} label="New this week" value={stats.new_this_week} />
        <StatCard icon={Trophy} label="Won" value={stats.won} />
        <StatCard icon={Eye} label="Site visits (7d)" value={stats.traffic_7d} />
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Traffic & leads · last 14 days</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={stats.timeline} margin={{ top: 8, right: 8, bottom: 0, left: -22 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltipStyle} />
                <Line type="monotone" dataKey="visits" stroke="#caff1a" strokeWidth={2} dot={false} name="Visits" />
                <Line type="monotone" dataKey="leads" stroke="#7db8ff" strokeWidth={2} dot={false} name="Leads" />
              </LineChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-medium text-white/70">Pipeline · leads by status</CardTitle>
          </CardHeader>
          <CardContent className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={statusChartData} margin={{ top: 8, right: 8, bottom: 0, left: -22 }}>
                <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
                <XAxis dataKey="name" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} />
                <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltipStyle} cursor={{ fill: "rgba(255,255,255,0.04)" }} />
                <Bar dataKey="count" fill="#caff1a" radius={[6, 6, 0, 0]} maxBarSize={42} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-4">
        <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-white/70">Recent leads</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5" data-testid="crm-recent-leads-table">
            {stats.recent_leads.length === 0 && <p className="text-sm text-white/40">No leads yet. They'll appear here the moment someone submits the consult form.</p>}
            {stats.recent_leads.map((lead) => (
              <div key={lead.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/25 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{lead.name}</p>
                  <p className="text-xs text-white/40 truncate">{lead.entity_type} · {lead.email || lead.phone}</p>
                </div>
                <Badge variant="outline" className={`shrink-0 text-[10px] uppercase tracking-wider ${STATUS_CLASSES[lead.status] || STATUS_CLASSES.new}`}>
                  {STATUS_LABELS[lead.status] || lead.status}
                </Badge>
              </div>
            ))}
          </CardContent>
        </Card>

        <Card className="bg-[color:var(--hs-bg-1)] border-white/10 rounded-2xl">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-medium text-white/70">Upcoming follow-ups</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2.5" data-testid="crm-upcoming-followups">
            {stats.upcoming_followups.length === 0 && <p className="text-sm text-white/40">No pending follow-ups. Set them from any lead's detail panel.</p>}
            {stats.upcoming_followups.map((fu) => (
              <div key={fu.id} className="flex items-center justify-between gap-3 rounded-xl border border-white/10 bg-black/25 px-4 py-3">
                <div className="min-w-0">
                  <p className="text-sm text-white truncate">{fu.title}</p>
                  <p className="text-xs text-white/40 truncate">{fu.lead_name}</p>
                </div>
                <span className="font-mono-ui text-xs text-[color:var(--hs-lime)] shrink-0">{fu.due_date}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};
